import { apiClient, PaginatedResponse } from './api';
import {
  Lead,
  CreateLeadData,
  UpdateLeadData,
  LeadFilters,
  CallAttempt,
  ImportLeadsData,
  ImportPhoneValidationResult,
  LeadImportResult,
  LeadImportJobStatus,
  LeadScrubJob,
  LeadScrubStartResult,
  LeadExportOptions,
  BulkLeadAction,
  ConversationStyleStats,
  CallAttemptStats,
  LeadStatus,
  LeadSubStage,
  LeadFollowUp,
  LeadNote,
  LeadTimelineEvent,
} from '../types/lead';
import { CampaignResultFilters, CampaignStats } from '../types/campaign';

const MAX_BULK_LEAD_IDS = 500;
const BULK_ACTION_TIMEOUT_MS = 60000;

function chunkIds<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export const leadService = {
  // Get aggregated summary for Results page (stats-only by default; pass includeLeads for legacy payload)
  async getResultsSummary(
    campaignId: string,
    filters?: CampaignResultFilters & { search?: string; includeLeads?: boolean }
  ): Promise<{
    stats: CampaignStats;
    callAttemptStats?: CallAttemptStats;
    conversationStyleStats?: ConversationStyleStats[];
    interestedLeads?: Lead[];
    notInterestedLeads?: Lead[];
    followUpLeads?: Lead[];
    appInLeads?: Lead[];
    appOutLeads?: Lead[];
    outOfBusinessLeads?: Lead[];
    wrongNumberLeads?: Lead[];
    unqualifiedLeads?: Lead[];
    gatekeeperLeads?: Lead[];
    operatorLeads?: Lead[];
    deceasedLeads?: Lead[];
    spanishLeads?: Lead[];
    newLeads?: Lead[];
    calledLeads?: Lead[];
    noAnswerLeads?: Lead[];
    unreachableLeads?: Lead[];
    failedLeads?: Lead[];
    doNotCallLeads?: Lead[];
    busyLeads?: Lead[];
  }> {
    const params = new URLSearchParams();
    if (filters?.startDateTime) params.append('startDateTime', filters.startDateTime.toISOString());
    if (filters?.endDateTime) params.append('endDateTime', filters.endDateTime.toISOString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.includeLeads) params.append('includeLeads', 'true');
    const queryString = params.toString();
    const url = `/results/campaigns/${campaignId}/summary${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data;
  },

  // Get unique lead statuses for a campaign
  async getLeadStatuses(campaignId: string): Promise<string[]> {
    const response = await apiClient.get(`/campaigns/${campaignId}/lead-statuses`);
    return response.data.data.statuses;
  },

  // Get leads for a campaign
  async getLeads(campaignId: string, filters?: LeadFilters): Promise<PaginatedResponse<Lead>> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status?.length) {
        filters.status.forEach(status => params.append('status', status));
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.timezone && filters.timezone !== 'all') params.append('timezone', filters.timezone);
      if (filters.hasEmail !== undefined) params.append('hasEmail', filters.hasEmail.toString());
      if (filters.lastCallBefore) params.append('lastCallBefore', filters.lastCallBefore);
      if (filters.lastCallAfter) params.append('lastCallAfter', filters.lastCallAfter);
      if (filters.createdBefore) params.append('createdBefore', filters.createdBefore);
      if (filters.createdAfter) params.append('createdAfter', filters.createdAfter);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const response = await apiClient.get<{
      leads: Lead[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/campaigns/${campaignId}/leads?${params.toString()}`, { timeout: 60000 });
    
    const responseData = response.data.data;
    return {
      success: response.data.success,
      data: responseData.leads,
      pagination: {
        page: responseData.pagination.page,
        limit: responseData.pagination.limit,
        total: responseData.pagination.total,
        totalPages: responseData.pagination.totalPages,
        hasNext: responseData.pagination.page < responseData.pagination.totalPages,
        hasPrev: responseData.pagination.page > 1
      }
    };
  },

  // Get all leads for the user
  async getAllLeads(filters?: LeadFilters): Promise<PaginatedResponse<Lead>> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status?.length) {
        filters.status.forEach(status => params.append('status', status));
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.subStageId) params.append('subStageId', filters.subStageId);
      if (filters.agentId) params.append('agentId', filters.agentId);
      if (filters.campaignId) params.append('campaignId', filters.campaignId);
      if (filters.followUpDateFrom) params.append('followUpDateFrom', filters.followUpDateFrom);
      if (filters.followUpDateTo) params.append('followUpDateTo', filters.followUpDateTo);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const response = await apiClient.get<{
      leads: Lead[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/leads/all?${params.toString()}`);
    
    const responseData = response.data.data;
    return {
      success: response.data.success,
      data: responseData.leads,
      pagination: {
        page: responseData.pagination.page,
        limit: responseData.pagination.limit,
        total: responseData.pagination.total,
        totalPages: responseData.pagination.totalPages,
        hasNext: responseData.pagination.page < responseData.pagination.totalPages,
        hasPrev: responseData.pagination.page > 1
      }
    };
  },

  // Per-status / per-sub-stage counts for the global Results page (tab + chip counts)
  async getLeadResultCounts(filters?: LeadFilters): Promise<{
    statusCounts: Record<string, number>;
    subStageCounts: Record<string, number>;
  }> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.status?.length) filters.status.forEach((s) => params.append('status', s));
      if (filters.search) params.append('search', filters.search);
      if (filters.agentId) params.append('agentId', filters.agentId);
      if (filters.campaignId) params.append('campaignId', filters.campaignId);
      if (filters.followUpDateFrom) params.append('followUpDateFrom', filters.followUpDateFrom);
      if (filters.followUpDateTo) params.append('followUpDateTo', filters.followUpDateTo);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
    }
    const response = await apiClient.get(`/leads/all/counts?${params.toString()}`);
    return response.data.data;
  },

  // Get lead by ID
  async getLead(leadId: string): Promise<Lead> {
    const response = await apiClient.get(`/leads/${leadId}`);
    return response.data.data.lead;
  },

  // Create new lead
  async createLead(campaignId: string, data: CreateLeadData): Promise<Lead> {
    const response = await apiClient.post(`/campaigns/${campaignId}/leads`, data);
    return response.data.data.lead;
  },

  // Create multiple leads
  async createLeads(campaignId: string, leads: CreateLeadData[]): Promise<Lead[]> {
    const response = await apiClient.post<Lead[]>(`/campaigns/${campaignId}/leads/batch`, {
      leads,
    });
    return response.data.data;
  },

  // Update lead
  async updateLead(leadId: string, data: UpdateLeadData): Promise<Lead> {
    const response = await apiClient.patch(`/leads/${leadId}`, data);
    return response.data.data.lead;
  },

  // Delete lead
  async deleteLead(leadId: string): Promise<void> {
    await apiClient.delete(`/leads/${leadId}`);
  },

  // Mark lead as do not call
  async markDoNotCall(leadId: string, reason?: string): Promise<Lead> {
    const response = await apiClient.post(`/leads/${leadId}/do-not-call`, {
      reason,
    });
    return response.data.data.lead;
  },

  // Remove do not call status
  async removeDoNotCall(leadId: string): Promise<Lead> {
    const response = await apiClient.delete<Lead>(`/leads/${leadId}/do-not-call`);
    return response.data.data;
  },

  // Check import phones for duplicates before uploading
  async validateImportPhones(
    campaignId: string,
    rows: Array<{ row: number; phone: string }>
  ): Promise<ImportPhoneValidationResult> {
    const response = await apiClient.post<ImportPhoneValidationResult>(
      `/campaigns/${campaignId}/leads/import/validate`,
      { rows }
    );
    return response.data.data;
  },

  // Import leads from file
  async importLeads(
    campaignId: string,
    data: ImportLeadsData,
    onProgress?: (status: LeadImportJobStatus) => void
  ): Promise<LeadImportResult> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('mapping', JSON.stringify(data.mapping));
    formData.append('skipDuplicates', data.skipDuplicates.toString());
    formData.append('updateExisting', data.updateExisting.toString());

    const response = await apiClient.upload<LeadImportResult>(
      `/campaigns/${campaignId}/leads/import`,
      formData,
      { timeout: 600_000 }
    );
    const result = response.data.data;

    if (result.async && result.importId) {
      return this.pollImportUntilComplete(campaignId, result.importId, onProgress);
    }

    return result;
  },

  async getImportStatus(campaignId: string, importId: string): Promise<LeadImportJobStatus> {
    const response = await apiClient.get<LeadImportJobStatus>(
      `/campaigns/${campaignId}/leads/import/${importId}/status`
    );
    return response.data.data;
  },

  async pollImportUntilComplete(
    campaignId: string,
    importId: string,
    onProgress?: (status: LeadImportJobStatus) => void
  ): Promise<LeadImportResult> {
    const maxWaitMs = 60 * 60 * 1000;
    const startedAt = Date.now();

    while (Date.now() - startedAt < maxWaitMs) {
      const status = await this.getImportStatus(campaignId, importId);
      onProgress?.(status);

      if (status.status === 'completed' || status.status === 'failed') {
        if (status.status === 'failed' && status.imported === 0) {
          throw new Error(status.errorMessage || 'Lead import failed');
        }
        return status;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    throw new Error('Import is still running. Please check back later from the campaign leads page.');
  },

  // Export leads
  async exportLeads(campaignId: string, options: LeadExportOptions): Promise<void> {
    const params = new URLSearchParams();
    params.append('format', options.format);
    options.fields.forEach(field => params.append('fields', field));
    
    if (options.filters) {
      if (options.filters.status?.length) {
        options.filters.status.forEach(status => params.append('status', status));
      }
      if (options.filters.search) params.append('search', options.filters.search);
      if (options.filters.hasEmail !== undefined) {
        params.append('hasEmail', options.filters.hasEmail.toString());
      }
      // Pass date/time filters when available
      if (options.filters.createdBefore) params.append('createdBefore', options.filters.createdBefore);
      if (options.filters.createdAfter) params.append('createdAfter', options.filters.createdAfter);
      if (options.filters.lastCallBefore) params.append('lastCallBefore', options.filters.lastCallBefore);
      if (options.filters.lastCallAfter) params.append('lastCallAfter', options.filters.lastCallAfter);
    }

    await apiClient.download(
      `/campaigns/${campaignId}/leads/export?${params.toString()}`,
      `leads-${campaignId}.${options.format}`
    );
  },

  // Bulk actions on leads. Delete is sent in capped batches so a full-page
  // (or larger) selection cannot stall the API or blow the request body.
  async bulkAction(action: BulkLeadAction): Promise<{ success: number; failed: number }> {
    const requestOptions = { timeout: BULK_ACTION_TIMEOUT_MS, skipErrorToast: true };

    if (action.action === 'delete') {
      let success = 0;
      let failed = 0;
      for (const leadIds of chunkIds(action.leadIds, MAX_BULK_LEAD_IDS)) {
        const response = await apiClient.post<{ success: number; failed: number }>(
          '/leads/bulk',
          { ...action, leadIds },
          requestOptions
        );
        success += Number(response.data.data?.success) || 0;
        failed += Number(response.data.data?.failed) || 0;
      }
      return { success, failed };
    }

    const response = await apiClient.post<{ success: number; failed: number }>(
      '/leads/bulk',
      action,
      requestOptions
    );
    return response.data.data;
  },

  // Bulk action on query results
  async bulkActionByQuery(action: {
    action: 'delete' | 'update_status' | 'do_not_call' | 'remove_do_not_call';
    campaignId: string;
    filters: LeadFilters;
    data?: { status?: LeadStatus; notes?: string };
  }): Promise<{ success: number; failed: number }> {
    const response = await apiClient.post<{ success: number; failed: number }>(
      '/leads/bulk-query',
      action,
      { timeout: 120000 }
    );
    return response.data.data;
  },

  // Get call attempts for a lead
  async getCallAttempts(leadId: string): Promise<CallAttempt[]> {
    const response = await apiClient.get(`/calls/leads/${leadId}/calls`);
    const calls = response.data.data.calls as CallAttempt[];
    return calls.map((call) => ({
      ...call,
      agentName:
        call.agentName ??
        call.agent_name ??
        (typeof call.captured_params === 'object' ? call.captured_params?.agentName : null) ??
        null,
    }));
  },

  // Get call attempt by ID
  async getCallAttempt(callId: string): Promise<CallAttempt> {
    const response = await apiClient.get<CallAttempt>(`/calls/${callId}`);
    return response.data.data;
  },

  // Manually trigger call for lead
  async triggerCall(leadId: string): Promise<CallAttempt> {
    const response = await apiClient.post<CallAttempt>(`/leads/${leadId}/call`);
    return response.data.data;
  },

  // Cancel ongoing call
  async cancelCall(callId: string): Promise<CallAttempt> {
    const response = await apiClient.post<CallAttempt>(`/calls/${callId}/cancel`);
    return response.data.data;
  },

  // Get interested leads
  async getInterestedLeads(campaignId: string, filters?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    startDateTime?: Date | undefined;
    endDateTime?: Date | undefined;
  }): Promise<PaginatedResponse<Lead & { capturedParams?: Record<string, any> }>> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.startDateTime) params.append('startDateTime', filters.startDateTime.toISOString());
      if (filters.endDateTime) params.append('endDateTime', filters.endDateTime.toISOString());
    }
    
    const queryString = params.toString();
    const url = `/results/campaigns/${campaignId}/interested${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<{
      success: boolean;
      data: {
        interestedLeads: (Lead & { capturedParams?: Record<string, any> })[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      };
    }>(url);
    
    const responseData = response.data.data as any;
    
    return {
        success: response.data.success,
        data: responseData.interestedLeads || responseData || [],
        pagination: {
          page: responseData.pagination?.page || 1,
          limit: responseData.pagination?.limit || 20,
          total: responseData.pagination?.total || 0,
          totalPages: responseData.pagination?.totalPages || 1,
          hasNext: (responseData.pagination?.page || 1) < (responseData.pagination?.totalPages || 1),
          hasPrev: (responseData.pagination?.page || 1) > 1
        }
      };
  },

  // Get not interested leads
  async getNotInterestedLeads(campaignId: string, filters?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    startDateTime?: Date | undefined;
    endDateTime?: Date | undefined;
  }): Promise<PaginatedResponse<Lead & { reason?: string }>> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.startDateTime) params.append('startDateTime', filters.startDateTime.toISOString());
      if (filters.endDateTime) params.append('endDateTime', filters.endDateTime.toISOString());
    }
    
    const queryString = params.toString();
    const url = `/results/campaigns/${campaignId}/not-interested${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<{
      success: boolean;
      data: {
        notInterestedLeads: (Lead & { reason?: string })[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      };
    }>(url);
    
    const responseData = response.data.data as any;
    
    return {
        success: response.data.success,
        data: responseData.notInterestedLeads || responseData || [],
        pagination: {
          page: responseData.pagination?.page || 1,
          limit: responseData.pagination?.limit || 20,
          total: responseData.pagination?.total || 0,
          totalPages: responseData.pagination?.totalPages || 1,
          hasNext: (responseData.pagination?.page || 1) < (responseData.pagination?.totalPages || 1),
          hasPrev: (responseData.pagination?.page || 1) > 1
        }
      };
  },

  // Validate phone numbers
  async validatePhoneNumbers(phoneNumbers: string[]): Promise<{
    valid: { phone: string; formatted: string }[];
    invalid: { phone: string; reason: string }[];
  }> {
    const response = await apiClient.post<{
      valid: { phone: string; formatted: string }[];
      invalid: { phone: string; reason: string }[];
    }>('/leads/validate-phones', { phoneNumbers });
    return response.data.data;
  },

  // Get duplicate leads
  async getDuplicateLeads(campaignId: string): Promise<{
    duplicates: Array<{
      phone: string;
      leads: Lead[];
    }>;
  }> {
    const response = await apiClient.get<{
      duplicates: Array<{
        phone: string;
        leads: Lead[];
      }>;
    }>(`/leads/campaigns/${campaignId}/leads/duplicates`);
    return response.data.data;
  },

  // Merge duplicate leads
  async mergeDuplicateLeads(primaryLeadId: string, duplicateLeadIds: string[]): Promise<Lead> {
    const response = await apiClient.post<Lead>(`/leads/${primaryLeadId}/merge`, {
      duplicateLeadIds,
    });
    return response.data.data;
  },

  // Get conversation style statistics for a campaign
  async getConversationStyleStats(campaignId: string, filters?: {
    startDateTime?: Date | undefined;
    endDateTime?: Date | undefined;
  }): Promise<ConversationStyleStats[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.startDateTime) params.append('startDateTime', filters.startDateTime.toISOString());
      if (filters.endDateTime) params.append('endDateTime', filters.endDateTime.toISOString());
    }
    
    const queryString = params.toString();
    const url = `/results/campaigns/${campaignId}/conversation-style-stats${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<ConversationStyleStats[]>(url);
    return response.data.data;
  },

  async getSubStages(params?: { campaignId?: string; mainStatus?: string }): Promise<LeadSubStage[]> {
    const query = new URLSearchParams();
    if (params?.campaignId) query.append('campaignId', params.campaignId);
    if (params?.mainStatus) query.append('mainStatus', params.mainStatus);
    const qs = query.toString();
    const response = await apiClient.get(`/leads/sub-stages${qs ? `?${qs}` : ''}`);
    return response.data.data.subStages;
  },

  async getCampaignsWithLeads(status?: string): Promise<string[]> {
    const params = status ? { status } : undefined;
    const response = await apiClient.get('/leads/campaigns-with-leads', { params });
    return response.data.data.campaignIds;
  },

  async getLeadTimeline(leadId: string): Promise<LeadTimelineEvent[]> {
    const response = await apiClient.get(`/leads/${leadId}/timeline`);
    return response.data.data.timeline;
  },

  async getLeadNotes(leadId: string): Promise<LeadNote[]> {
    const response = await apiClient.get(`/leads/${leadId}/notes`);
    const notes = response.data.data.notes as Array<LeadNote & { created_by_name?: string | null }>;
    return notes.map((note) => ({
      ...note,
      createdBy: note.createdBy ?? (note as { created_by?: string | null }).created_by ?? null,
      createdByName:
        note.createdByName ??
        note.created_by_name ??
        null,
    }));
  },

  async addLeadNote(leadId: string, body: string): Promise<LeadNote> {
    const response = await apiClient.post(`/leads/${leadId}/notes`, { body });
    const note = response.data.data.note as LeadNote & { created_by_name?: string | null };
    return {
      ...note,
      createdByName: note.createdByName ?? note.created_by_name ?? null,
    };
  },

  async updateLeadNote(leadId: string, noteId: string, body: string): Promise<LeadNote> {
    const response = await apiClient.patch(`/leads/${leadId}/notes/${noteId}`, { body });
    return response.data.data.note;
  },

  async deleteLeadNote(leadId: string, noteId: string): Promise<void> {
    await apiClient.delete(`/leads/${leadId}/notes/${noteId}`);
  },

  async addLeadFollowUp(leadId: string, scheduledAt: string, notes?: string): Promise<void> {
    await apiClient.post(`/leads/${leadId}/follow-ups`, { scheduledAt, notes, updateLegacyDate: true });
  },

  async getLeadFollowUps(leadId: string): Promise<LeadFollowUp[]> {
    const response = await apiClient.get(`/leads/${leadId}/follow-ups`);
    return response.data.data.followUps;
  },

  async startPhoneLookup(
    campaignId: string,
    payload: { statuses?: string[]; leadIds?: string[]; timezones?: string[] }
  ): Promise<LeadScrubStartResult> {
    const response = await apiClient.post(`/campaigns/${campaignId}/leads/phone-lookup`, payload);
    return response.data.data;
  },

  async getLatestPhoneLookup(campaignId: string): Promise<LeadScrubJob | null> {
    const response = await apiClient.get(`/campaigns/${campaignId}/leads/phone-lookup/latest`);
    return response.data.data ?? null;
  },

  async getPhoneLookupJob(campaignId: string, jobId: string): Promise<LeadScrubJob> {
    const response = await apiClient.get(`/campaigns/${campaignId}/leads/phone-lookup/${jobId}`);
    return response.data.data;
  },

  async startDncScrub(
    campaignId: string,
    payload: { statuses?: string[]; leadIds?: string[]; timezones?: string[] }
  ): Promise<LeadScrubStartResult> {
    const response = await apiClient.post(`/campaigns/${campaignId}/leads/dnc`, payload);
    return response.data.data;
  },

  async getLatestDncScrub(campaignId: string): Promise<LeadScrubJob | null> {
    const response = await apiClient.get(`/campaigns/${campaignId}/leads/dnc/latest`);
    return response.data.data ?? null;
  },

  async getDncScrubJob(campaignId: string, jobId: string): Promise<LeadScrubJob> {
    const response = await apiClient.get(`/campaigns/${campaignId}/leads/dnc/${jobId}`);
    return response.data.data;
  },
};